import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import { Button, Card, CardBody, Col, Form, Row } from "react-bootstrap";
import * as yup from "yup";
import { ISupplier } from "../../shared/interfaces/supplier/ISupplier";
import { SupplierService } from "./service";
import { useUnsavedChanges } from "../../shared/hooks/useUnsavedChanges";
import {
    blockNegativeKey,
    blockNegativePaste,
    isNegativeInput,
} from "../../shared/utils/functions";

const schema = yup.object().shape({
    name: yup.string().required("validation.required"),
    // A negative discount would raise the purchase price, not lower it
    specialDiscount: yup
        .number()
        .notRequired()
        .min(0, "validation.negativeDiscount"),
});

const SupplierForm = () => {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const isEdit = Boolean(id);
    const navigate = useNavigate();
    const {
        control,
        handleSubmit,
        reset,
        formState: { errors, isDirty },
    } = useForm<ISupplier>({
        resolver: yupResolver(schema) as any,
        defaultValues: isEdit
            ? async () => {
                try {
                    return await SupplierService.getSingle(+id!);
                } catch (e) {
                    ErrorToast(e);
                }
            }
            : {
                name: "",
                specialDiscount: undefined,
            },
    });

    const { guardedNavigate, unsavedChangesModal } = useUnsavedChanges(isDirty);

    const onSubmit = async (data: ISupplier) => {
        try {
            if (isEdit) {
                await SupplierService.update(data, +id!);
            } else {
                await SupplierService.create(data);
            }
            SuccessToast(
                t(`supplier.${isEdit ? "successUpdate" : "successCreate"}`),
            );
            navigate("/suppliers");
            reset();
        } catch (error) {
            ErrorToast(error);
        }
    };

    return (
        <React.Fragment>
            <Row>
                <Col sm={12}>
                    <Card>
                        <CardBody>
                            <Form onSubmit={handleSubmit(onSubmit)}>
                                <Row className="g-3">
                                    <Col sm={12} xl={6}>
                                        <Form.Label className={"required"}>
                                            {t("supplier.name")}
                                        </Form.Label>
                                        <Controller
                                            name="name"
                                            control={control}
                                            render={({ field, fieldState }) => (
                                                <>
                                                    <Form.Control
                                                        type="text"
                                                        isInvalid={!!fieldState.error}
                                                        placeholder={t("supplier.enterName")}
                                                        {...field}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {t(fieldState.error?.message as string)}
                                                    </Form.Control.Feedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col sm={12} xl={6}>
                                        <Form.Label>
                                            {t("supplier.specialDiscount")}
                                        </Form.Label>
                                        <Controller
                                            name="specialDiscount"
                                            control={control}
                                            render={({ field, fieldState }) => (
                                                <>
                                                    <Form.Control
                                                        type="number"
                                                        step={0.01}
                                                        min={0}
                                                        isInvalid={!!fieldState.error}
                                                        placeholder={t("supplier.enterSpecialDiscount")}
                                                        value={field.value ?? ""}
                                                        onKeyDown={blockNegativeKey}
                                                        onPaste={blockNegativePaste}
                                                        onChange={(e) => {
                                                            if (isNegativeInput(e.target.value)) return;
                                                            field.onChange(
                                                                e.target.value === ""
                                                                    ? undefined
                                                                    : Number(e.target.value),
                                                            );
                                                        }}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {t(fieldState.error?.message as string)}
                                                    </Form.Control.Feedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col sm={12} className="d-flex gap-2">
                                        <Button type="submit" variant="primary">
                                            {t(`supplier.${isEdit ? "update" : "create"}`)}
                                        </Button>
                                        <Button type="button" variant="secondary" onClick={() => guardedNavigate("/suppliers")}>
                                            {t("button.cancel")}
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            {unsavedChangesModal}
        </React.Fragment>
    );
};

export default SupplierForm;
